/**
 * Kind-aware deep-link target of an in-app notification (NotificationBell).
 * Kept in its own module so the router-link resolution test can assert every
 * target resolves to a real route in app/router.tsx.
 */

import type { AppNotification } from '@/lib/api/notifications';

/** Where a notification navigates on click, or null when there's no target.
 * AI-staff kinds route by `kind`/`type` first (their `entity_type` is almost
 * always the generic `agent_task`, which doesn't tell us WHICH AI-team
 * surface is relevant on its own — see FE-LANE 5 report for the route
 * rationale). Collaboration kinds keep the existing entity_type-first
 * routing: share notifications carry `entity_type` (the scope:
 * group|contact|all) + `entity_id` (the group/contact id) so we can open
 * exactly what was shared. */
export function targetFor(n: AppNotification): string | null {
  const k = n.kind || n.type || '';
  if (k === 'staff_question') return '/staff-questions';
  if (k === 'staff_decision_needed') return '/approvals';
  if (k === 'staff_escalation') return '/ai-team/control';
  if (k === 'staff_delivery') return '/ai-team/deliverables';
  if (k === 'mas_persona_action') return '/ai-activity';

  // Publishing notifications (Epic 2 2c). The publish-job approval queue
  // (jobs submitted with requires_approval → awaiting_approval) is the
  // ApprovalQueuePanel at the top of /publishing/history — NOT /inbox/approvals,
  // which is the AI reply-draft queue (community drafts).
  if (k === 'post.approval_requested' || k === 'post.approved' || k === 'post.rejected') {
    return '/publishing/history';
  }
  if (k === 'post.reminder') return '/calendar';
  if (k === 'post.published' || k === 'post.failed') return '/publishing/history';

  const t = n.entity_type;
  const id = n.entity_id;
  if (t === 'group' && id) return `/crm/contacts?group_id=${encodeURIComponent(id)}`;
  if (t === 'contact' && id) return `/crm/contacts?contact_id=${encodeURIComponent(id)}`;
  if (t === 'all') return '/crm/contacts?shared=1';
  if (k.includes('share')) return '/crm/contacts?shared=1';
  if (k.includes('lead_assigned') || k.includes('assign'))
    return id ? `/crm/contacts?contact_id=${encodeURIComponent(id)}` : '/crm/contacts';
  if (k.includes('invite') || k.includes('role') || k.includes('member')) return '/settings/team';
  // Any future/unclassified AI-staff kind still lands somewhere useful.
  if (t === 'agent_task' || k.startsWith('mas_') || k.startsWith('staff_')) return '/ai-activity';
  return null;
}
